import moment from 'moment'

import type {
  DateRange,
  FilterOption,
  FilterOptionDate,
  FilterOptionRange,
} from 'features/filters/types'
import {
  formatDateToUTCEndOfDay,
  formatDateToUTCMidnight,
  HardcodedFilterOptionsEnum,
} from 'features/filters/utils/helpers'
import { findDateWindow } from 'features/project/utils'
import type { DateWindows, Filters } from 'types/project'

export type UnixRange = { min: number; max: number }

/**
 * The period immediately before `current`, for a prior-period comparison.
 *
 * - A named window (a month, quarter, year, ...) steps back to the previous
 *   entry in its own list, so "March" compares against "February", not against
 *   a 31-day slice. `Trailing` is excluded: it has no entry for the preceding
 *   span, so it shifts back by its own length instead.
 * - A trailing/custom range shifts back by its duration.
 * - `isBeforeFirstReview` (and no `range`) when the shifted start precedes the
 *   project's earliest review — there is no data to compare against.
 *
 * If a named window's list is missing from `dateWindows` (not loaded yet) the
 * current range is returned unshifted, matching what `usePrevFilters` has
 * always done.
 */
export function derivePriorDateRange(
  current: UnixRange,
  dateWindows: DateWindows,
  defaultFilterList: FilterOption[],
): { range: UnixRange | undefined; isBeforeFirstReview: boolean } {
  const foundDate = findDateWindow(dateWindows, current.min, current.max)

  if (foundDate?.type && foundDate.type !== 'Trailing') {
    const dateArr = dateWindows[foundDate.type]
    if (!dateArr) return { range: current, isBeforeFirstReview: false }
    const foundIndex = dateArr.findIndex(
      (el: DateRange) => el.label === foundDate.label,
    )
    const prevIndex =
      foundIndex !== dateArr.length - 1 ? foundIndex + 1 : dateArr.length - 1
    return {
      range: JSON.parse(dateArr[prevIndex]!.value) as UnixRange,
      isBeforeFirstReview: false,
    }
  }

  const timeDiff = Math.abs(current.max - current.min)
  const min = formatDateToUTCMidnight(
    moment.unix(current.min - timeDiff),
  ).unix()
  const max = formatDateToUTCEndOfDay(
    moment.unix(current.max - timeDiff),
  ).unix()
  const firstReview = (
    defaultFilterList.find((el) => el.field === 'create_time') as
      | FilterOptionRange
      | undefined
  )?.min
  if (firstReview !== undefined && min < firstReview) {
    return { range: undefined, isBeforeFirstReview: true }
  }
  return { range: { min, max }, isBeforeFirstReview: false }
}

/**
 * The date window a v1 `Filters` carries (its `create_time` entry), as the
 * `{ startDate, endDate }` a v2 panel uses — for callers that still hold v1
 * filters and need to hand a date to a v2-shaped component.
 */
export function getCreateTimeRange(
  filters: Filters | null | undefined,
): { startDate: number; endDate: number } | null {
  const option = filters?.values?.find(
    ({ field }) => field === HardcodedFilterOptionsEnum.CREATE_TIME,
  ) as FilterOptionDate | undefined
  return option ? { startDate: option.min, endDate: option.max } : null
}
